/**
 * Oppgaveoversikt - Komplett oversikt over alle oppgaver
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './Oppgaveoversikt.css';

export default function Oppgaveoversikt() {
  const [oppgaver, setOppgaver] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle'); // 'alle', 'ikke_startet', 'i_gang', 'fullført'
  const [sortering, setSortering] = useState('forfall_dato'); // 'forfall_dato', 'prioritet', 'opprettet_dato'
  const [redigerOppgave, setRedigerOppgave] = useState(null);
  const [nyOppgave, setNyOppgave] = useState(false);
  const [saker, setSaker] = useState([]);
  const [klienter, setKlienter] = useState([]);
  const [valgteOppgaver, setValgteOppgaver] = useState([]);
  const [sokTekst, setSokTekst] = useState('');
  const [typeFilter, setTypeFilter] = useState('alle');

  useEffect(() => {
    lastOppgaver();
    lastSaker();
    lastKlienter();
  }, []);

  const lastOppgaver = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getOppgaver();
      setOppgaver(data.oppgaver || []);
    } catch (error) {
      console.error('Feil ved lasting av oppgaver:', error);
      alert('Kunne ikke laste oppgaver');
    } finally {
      setLoading(false);
    }
  };

  const lastSaker = async () => {
    try {
      const data = await crmApi.getSaker();
      setSaker(data.saker || []);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
    }
  };

  const lastKlienter = async () => {
    try {
      const data = await crmApi.getKlienter();
      setKlienter(data.klienter || []);
    } catch (error) {
      console.error('Feil ved lasting av klienter:', error);
    }
  };

  const handleSlettOppgave = async (oppgaveId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppgaven?')) {
      return;
    }

    try {
      await crmApi.deleteOppgave(oppgaveId);
      alert('✅ Oppgave slettet');
      lastOppgaver();
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Kunne ikke slette oppgave');
    }
  };

  const handleLagre = async (oppgaveData) => {
    try {
      if (redigerOppgave) {
        await crmApi.updateOppgave(redigerOppgave.id, oppgaveData);
        alert('✅ Oppgave oppdatert');
      } else {
        await crmApi.createOppgave(oppgaveData);
        alert('✅ Oppgave opprettet');
      }
      setRedigerOppgave(null);
      setNyOppgave(false);
      lastOppgaver();
    } catch (error) {
      console.error('Feil ved lagring:', error);
      alert('Kunne ikke lagre oppgave: ' + error.message);
    }
  };

  const handleMarkerStatus = async (oppgave, nyStatus) => {
    try {
      await crmApi.updateOppgave(oppgave.id, {
        ...oppgave,
        status: nyStatus,
        ...(nyStatus === 'fullført' && !oppgave.fullført_dato ? { fullført_dato: new Date().toISOString().split('T')[0] } : {}),
        ...(nyStatus === 'i_gang' && !oppgave.startet_dato ? { startet_dato: new Date().toISOString().split('T')[0] } : {})
      });
      lastOppgaver();
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Kunne ikke oppdatere status');
    }
  };

  const toggleValgOppgave = (oppgaveId) => {
    setValgteOppgaver(prev =>
      prev.includes(oppgaveId)
        ? prev.filter(id => id !== oppgaveId)
        : [...prev, oppgaveId]
    );
  };

  const toggleValgAlle = () => {
    if (valgteOppgaver.length === sorterteOppgaver.length) {
      setValgteOppgaver([]);
    } else {
      setValgteOppgaver(sorterteOppgaver.map(o => o.id));
    }
  };

  const handleEksporterCSV = () => {
    // Generer CSV-data med filtrerte oppgaver
    const headers = ['Tittel', 'Type', 'Status', 'Prioritet', 'Sak', 'Klient', 'Forfallsdato', 'Estimerte timer', 'Ansvarlig'];
    const csvData = sorterteOppgaver.map(oppgave => [
      oppgave.tittel || '',
      oppgave.type || '',
      oppgave.status || '',
      oppgave.prioritet || '',
      oppgave.saksnummer ? `${oppgave.saksnummer} - ${oppgave.sak_tittel || ''}` : '',
      oppgave.klient_navn || '',
      oppgave.forfall_dato || oppgave.forfallsdato ? new Date(oppgave.forfall_dato || oppgave.forfallsdato).toLocaleDateString('nb-NO') : '',
      oppgave.estimert_timer || '',
      oppgave.ansvarlig_advokat || ''
    ]);

    // Kombiner headers og data
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Last ned som fil
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `oppgaver_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkSlett = async () => {
    if (valgteOppgaver.length === 0) return;

    if (!window.confirm(`Er du sikker på at du vil slette ${valgteOppgaver.length} oppgaver?`)) {
      return;
    }

    let slettet = 0;
    let feilet = 0;
    const feilmeldinger = [];

    // Slett én og én for bedre feilhåndtering
    for (const id of valgteOppgaver) {
      try {
        await crmApi.deleteOppgave(id);
        slettet++;
      } catch (error) {
        feilet++;
        feilmeldinger.push(`Oppgave ${id}: ${error.message}`);
        console.error(`Feil ved sletting av oppgave ${id}:`, error);
      }
    }

    // Vis resultat
    if (feilet === 0) {
      alert(`✅ ${slettet} oppgaver slettet`);
    } else if (slettet === 0) {
      alert(`❌ Kunne ikke slette noen oppgaver.\n\n${feilmeldinger.join('\n')}`);
    } else {
      alert(`⚠️ ${slettet} oppgaver slettet, ${feilet} feilet.\n\n${feilmeldinger.join('\n')}`);
    }

    setValgteOppgaver([]);
    lastOppgaver();
  };

  const handleBulkEndreStatus = async (nyStatus) => {
    if (valgteOppgaver.length === 0) return;

    try {
      const oppdateringer = valgteOppgaver.map(id => {
        const oppgave = oppgaver.find(o => o.id === id);
        return crmApi.updateOppgave(id, {
          ...oppgave,
          status: nyStatus,
          ...(nyStatus === 'fullført' && !oppgave.fullført_dato ? { fullført_dato: new Date().toISOString().split('T')[0] } : {}),
          ...(nyStatus === 'i_gang' && !oppgave.startet_dato ? { startet_dato: new Date().toISOString().split('T')[0] } : {})
        });
      });

      await Promise.all(oppdateringer);
      alert(`✅ ${valgteOppgaver.length} oppgaver oppdatert`);
      setValgteOppgaver([]);
      lastOppgaver();
    } catch (error) {
      console.error('Feil ved bulk oppdatering:', error);
      alert('Kunne ikke oppdatere alle oppgaver');
    }
  };

  const filtrerteOppgaver = oppgaver.filter(o => {
    // Status filter
    if (filter !== 'alle' && o.status !== filter) return false;

    // Type filter
    if (typeFilter !== 'alle' && o.type !== typeFilter) return false;

    // Søketekst filter
    if (sokTekst) {
      const sok = sokTekst.toLowerCase();
      const matchTittel = o.tittel?.toLowerCase().includes(sok);
      const matchBeskrivelse = o.beskrivelse?.toLowerCase().includes(sok);
      const matchSak = o.saksnummer?.toLowerCase().includes(sok);
      const matchKlient = o.klient_navn?.toLowerCase().includes(sok);

      if (!matchTittel && !matchBeskrivelse && !matchSak && !matchKlient) return false;
    }

    return true;
  });

  const sorterteOppgaver = [...filtrerteOppgaver].sort((a, b) => {
    if (sortering === 'forfall_dato') {
      if (!a.forfall_dato && !a.forfallsdato) return 1;
      if (!b.forfall_dato && !b.forfallsdato) return -1;
      const dateA = new Date(a.forfall_dato || a.forfallsdato);
      const dateB = new Date(b.forfall_dato || b.forfallsdato);
      return dateA - dateB;
    } else if (sortering === 'prioritet') {
      const prioritetOrder = { 'kritisk': 4, 'høy': 3, 'middels': 2, 'lav': 1 };
      return (prioritetOrder[b.prioritet] || 2) - (prioritetOrder[a.prioritet] || 2);
    } else if (sortering === 'opprettet_dato') {
      return b.opprettet_dato - a.opprettet_dato;
    }
    return 0;
  });

  const getPrioritetFarge = (prioritet) => {
    const farger = {
      'kritisk': 'priority-kritisk',
      'høy': 'priority-hoy',
      'middels': 'priority-middels',
      'lav': 'priority-lav'
    };
    return farger[prioritet] || 'priority-middels';
  };

  const getStatusFarge = (status) => {
    const farger = {
      'ikke_startet': 'status-ikke-startet',
      'i_gang': 'status-i-gang',
      'fullført': 'status-fullfort',
      'blokkert': 'status-blokkert'
    };
    return farger[status] || 'status-ikke-startet';
  };

  const getDagerTilForfall = (forfallDato) => {
    if (!forfallDato) return null;
    const now = new Date();
    const forfall = new Date(forfallDato);
    const diff = Math.ceil((forfall - now) / (1000 * 60 * 60 * 24));

    if (diff < 0) return { tekst: `${Math.abs(diff)} dager over`, klasse: 'overdue' };
    if (diff === 0) return { tekst: 'I dag', klasse: 'today' };
    if (diff === 1) return { tekst: 'I morgen', klasse: 'tomorrow' };
    if (diff <= 7) return { tekst: `Om ${diff} dager`, klasse: 'soon' };
    return { tekst: `Om ${diff} dager`, klasse: 'normal' };
  };

  const formaterStatusNavn = (status) => {
    const navn = {
      'ikke_startet': 'Ikke startet',
      'i_gang': 'I gang',
      'fullført': 'Fullført',
      'blokkert': 'Blokkert'
    };
    return navn[status] || status;
  };

  if (loading) {
    return (
      <div className="oppgaveoversikt loading">
        <div className="spinner"></div>
        <p>Laster oppgaver...</p>
      </div>
    );
  }

  return (
    <div className="oppgaveoversikt">
      <div className="oppgave-header">
        <h1>📋 Oppgaveoversikt</h1>
        <div style={{display: 'flex', gap: '0.75rem'}}>
          <button className="btn-secondary" onClick={handleEksporterCSV} title="Eksporter oppgaveliste til CSV">
            📊 Eksporter CSV
          </button>
          <button className="btn-primary" onClick={() => setNyOppgave(true)}>
            ➕ Ny oppgave
          </button>
        </div>
      </div>

      {/* Søk og filter */}
      <div className="oppgave-sok" style={{marginBottom: '1rem'}}>
        <input
          type="text"
          placeholder="🔍 Søk i oppgaver (tittel, beskrivelse, sak, klient)..."
          value={sokTekst}
          onChange={(e) => setSokTekst(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}
        />
      </div>

      {/* Statistikk-kort */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: '8px', padding: '1rem'}}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#d97706'}}>
            {sorterteOppgaver.filter(o => o.status === 'ikke_startet').length}
          </div>
          <div style={{color: '#92400e', fontSize: '0.9rem'}}>📋 Ikke startet</div>
        </div>

        <div style={{background: '#dbeafe', border: '2px solid #3b82f6', borderRadius: '8px', padding: '1rem'}}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#1d4ed8'}}>
            {sorterteOppgaver.filter(o => o.status === 'i_gang').length}
          </div>
          <div style={{color: '#1e40af', fontSize: '0.9rem'}}>⚡ I gang</div>
        </div>

        <div style={{background: '#f0fdf4', border: '2px solid #22c55e', borderRadius: '8px', padding: '1rem'}}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#16a34a'}}>
            {sorterteOppgaver.filter(o => o.status === 'fullført').length}
          </div>
          <div style={{color: '#15803d', fontSize: '0.9rem'}}>✅ Fullført</div>
        </div>

        <div style={{background: '#fef2f2', border: '2px solid #ef4444', borderRadius: '8px', padding: '1rem'}}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#dc2626'}}>
            {sorterteOppgaver.filter(o => o.kritisk_frist === 1 && o.status !== 'fullført').length}
          </div>
          <div style={{color: '#991b1b', fontSize: '0.9rem'}}>🚨 Kritiske frister</div>
        </div>
      </div>

      {/* Hurtigfiltre */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        flexWrap: 'wrap'
      }}>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter('i_gang');
            setTypeFilter('alle');
            setSortering('forfall_dato');
            setSokTekst('');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: filter === 'i_gang' && typeFilter === 'alle' && !sokTekst ? '#3b82f6' : '#f3f4f6',
            color: filter === 'i_gang' && typeFilter === 'alle' && !sokTekst ? 'white' : '#374151'
          }}
        >
          ⚡ Mine pågående
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter('ikke_startet');
            setTypeFilter('alle');
            setSortering('prioritet');
            setSokTekst('');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          📋 Ventende oppgaver
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter('alle');
            setTypeFilter('alle');
            setSortering('forfall_dato');
            setSokTekst('');
            // Filtrer kun oppgaver med kritisk frist
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#fef2f2',
            color: '#991b1b',
            border: '1px solid #ef4444'
          }}
        >
          🚨 Kritiske frister
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter('fullført');
            setTypeFilter('alle');
            setSortering('opprettet_dato');
            setSokTekst('');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          ✅ Nylig fullført
        </button>
        <button
          className="btn-secondary"
          onClick={() => {
            setFilter('alle');
            setTypeFilter('alle');
            setSortering('forfall_dato');
            setSokTekst('');
          }}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            background: '#f3f4f6',
            color: '#374151'
          }}
        >
          🔄 Nullstill filtre
        </button>
      </div>

      {/* Filter og sorteringskontroller */}
      <div className="oppgave-kontroller">
        <div className="filter-buttons">
          <button
            className={filter === 'alle' ? 'active' : ''}
            onClick={() => setFilter('alle')}
          >
            Alle ({oppgaver.length})
          </button>
          <button
            className={filter === 'ikke_startet' ? 'active' : ''}
            onClick={() => setFilter('ikke_startet')}
          >
            Ikke startet ({oppgaver.filter(o => o.status === 'ikke_startet').length})
          </button>
          <button
            className={filter === 'i_gang' ? 'active' : ''}
            onClick={() => setFilter('i_gang')}
          >
            I gang ({oppgaver.filter(o => o.status === 'i_gang').length})
          </button>
          <button
            className={filter === 'fullført' ? 'active' : ''}
            onClick={() => setFilter('fullført')}
          >
            Fullført ({oppgaver.filter(o => o.status === 'fullført').length})
          </button>
        </div>

        <div className="type-filter" style={{marginTop: '1rem'}}>
          <label style={{marginRight: '0.5rem', fontWeight: 'bold'}}>Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd'}}
          >
            <option value="alle">Alle typer</option>
            <option value="todo">TODO</option>
            <option value="møte">Møte</option>
            <option value="frist">Frist</option>
            <option value="dokument">Dokument</option>
            <option value="oppfølging">Oppfølging</option>
          </select>
        </div>

        <div className="sortering-buttons">
          <label>Sorter etter:</label>
          <button
            className={sortering === 'forfall_dato' ? 'active' : ''}
            onClick={() => setSortering('forfall_dato')}
          >
            📅 Forfallsdato
          </button>
          <button
            className={sortering === 'prioritet' ? 'active' : ''}
            onClick={() => setSortering('prioritet')}
          >
            ⚡ Prioritet
          </button>
          <button
            className={sortering === 'opprettet_dato' ? 'active' : ''}
            onClick={() => setSortering('opprettet_dato')}
          >
            🕒 Opprettet
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {valgteOppgaver.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span>{valgteOppgaver.length} oppgaver valgt</span>
            <button className="btn-text" onClick={() => setValgteOppgaver([])}>
              Avbryt
            </button>
          </div>
          <div className="bulk-buttons">
            <button className="btn-bulk" onClick={() => handleBulkEndreStatus('ikke_startet')}>
              Marker som ikke startet
            </button>
            <button className="btn-bulk" onClick={() => handleBulkEndreStatus('i_gang')}>
              Marker som i gang
            </button>
            <button className="btn-bulk" onClick={() => handleBulkEndreStatus('fullført')}>
              Marker som fullført
            </button>
            <button className="btn-bulk btn-bulk-delete" onClick={handleBulkSlett}>
              🗑️ Slett valgte
            </button>
          </div>
        </div>
      )}

      {/* Velg alle knapp */}
      {sorterteOppgaver.length > 0 && (
        <div className="select-all-bar">
          <label>
            <input
              type="checkbox"
              checked={valgteOppgaver.length === sorterteOppgaver.length && sorterteOppgaver.length > 0}
              onChange={toggleValgAlle}
            />
            <span>Velg alle ({sorterteOppgaver.length})</span>
          </label>
        </div>
      )}

      {/* Oppgaveliste */}
      <div className="oppgave-liste">
        {sorterteOppgaver.length === 0 ? (
          <div className="empty-state">
            <p>Ingen oppgaver {filter !== 'alle' ? `med status "${formaterStatusNavn(filter)}"` : ''}</p>
          </div>
        ) : (
          sorterteOppgaver.map(oppgave => {
            const dagerTil = getDagerTilForfall(oppgave.forfall_dato || oppgave.forfallsdato);
            const erValgt = valgteOppgaver.includes(oppgave.id);

            return (
              <div key={oppgave.id} className={`oppgave-card ${oppgave.status} ${erValgt ? 'selected' : ''}`}>
                <div className="oppgave-checkbox">
                  <input
                    type="checkbox"
                    checked={erValgt}
                    onChange={() => toggleValgOppgave(oppgave.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="oppgave-status-indicator">
                  <select
                    value={oppgave.status}
                    onChange={(e) => handleMarkerStatus(oppgave, e.target.value)}
                    className={getStatusFarge(oppgave.status)}
                  >
                    <option value="ikke_startet">Ikke startet</option>
                    <option value="i_gang">I gang</option>
                    <option value="fullført">Fullført</option>
                    <option value="blokkert">Blokkert</option>
                  </select>
                </div>

                <div className="oppgave-innhold">
                  <div className="oppgave-topp">
                    <div className="oppgave-topp-left">
                      <h3>
                        {oppgave.kritisk_frist === 1 && <span style={{color: '#ef4444', marginRight: '0.5rem'}}>🚨</span>}
                        {oppgave.tittel}
                      </h3>
                      {/* Sak/Klient info direkte under tittel */}
                      {(oppgave.saksnummer || oppgave.klient_navn) && (
                        <div className="oppgave-sak-klient-info">
                          {oppgave.saksnummer && (
                            <span className="sak-badge">
                              📁 {oppgave.saksnummer}{oppgave.sak_tittel ? ` - ${oppgave.sak_tittel}` : ''}
                            </span>
                          )}
                          {oppgave.klient_navn && (
                            <span className="klient-badge">
                              👤 {oppgave.klient_navn}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className={`prioritet-badge ${getPrioritetFarge(oppgave.prioritet)}`}>
                      {oppgave.prioritet}
                    </span>
                  </div>

                  {oppgave.beskrivelse && (
                    <p className="oppgave-beskrivelse">{oppgave.beskrivelse}</p>
                  )}

                  <div className="oppgave-meta">
                    {(oppgave.forfall_dato || oppgave.forfallsdato) && dagerTil && (
                      <span className={`oppgave-forfall ${dagerTil.klasse}`}>
                        📅 {new Date(oppgave.forfall_dato || oppgave.forfallsdato).toLocaleDateString('nb-NO')} ({dagerTil.tekst})
                      </span>
                    )}
                    {oppgave.ansvarlig_advokat && (
                      <span className="oppgave-ansvarlig">
                        👨‍💼 {oppgave.ansvarlig_advokat}
                      </span>
                    )}
                    <span className="oppgave-type">
                      {getTypeIkon(oppgave.type)} {oppgave.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div className="oppgave-actions">
                  <button
                    className="btn-edit"
                    onClick={() => setRedigerOppgave(oppgave)}
                    title="Rediger"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleSlettOppgave(oppgave.id)}
                    title="Slett"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal for ny/redigert oppgave */}
      {(nyOppgave || redigerOppgave) && (
        <OppgaveModal
          oppgave={redigerOppgave}
          saker={saker}
          klienter={klienter}
          onClose={() => {
            setNyOppgave(false);
            setRedigerOppgave(null);
          }}
          onSave={handleLagre}
        />
      )}
    </div>
  );
}

function OppgaveModal({ oppgave, saker, klienter, onClose, onSave }) {
  const [formData, setFormData] = useState({
    sak_id: oppgave?.sak_id || '',
    klient_id: oppgave?.klient_id || '',
    tittel: oppgave?.tittel || '',
    beskrivelse: oppgave?.beskrivelse || '',
    type: oppgave?.type || 'todo',
    prioritet: oppgave?.prioritet || 'middels',
    ansvarlig_advokat: oppgave?.ansvarlig_advokat || '',
    estimert_timer: oppgave?.estimert_timer || 1,
    forfall_dato: (typeof oppgave?.forfall_dato === 'string' ? oppgave.forfall_dato.split('T')[0] : null) ||
                  (typeof oppgave?.forfallsdato === 'string' ? oppgave.forfallsdato.split('T')[0] : null) || '',
    status: oppgave?.status || 'ikke_startet',
    kritisk_frist: oppgave?.kritisk_frist === 1 || oppgave?.kritisk_frist === true || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.tittel || !formData.type) {
      alert('Tittel og type er påkrevd');
      return;
    }

    if (!formData.sak_id && !formData.klient_id && formData.type !== 'todo') {
      alert('Du må velge enten sak eller klient');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content oppgave-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>{oppgave ? '✏️ Rediger oppgave' : '➕ Ny oppgave'}</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group required">
            <label>Tittel *</label>
            <input
              type="text"
              value={formData.tittel}
              onChange={(e) => setFormData({ ...formData, tittel: e.target.value })}
              placeholder="F.eks. Gjennomgå kontrakt"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group required">
              <label>Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="todo">Generell TODO</option>
                <optgroup label="Prosessdokumenter">
                  <option value="rettsmøte">Rettsmøte</option>
                  <option value="stevning">Stevning</option>
                  <option value="tilsvar">Tilsvar</option>
                  <option value="prosesskriv">Prosesskriv</option>
                  <option value="spesifisert_krav">Spesifisert krav</option>
                  <option value="søksmålsvarsel">Søksmålsvarsel</option>
                  <option value="avsluttet_saksforberedelse">Avsluttet saksforberedelse</option>
                  <option value="sluttinnlegg">Sluttinnlegg</option>
                </optgroup>
                <optgroup label="Kommunikasjon">
                  <option value="møte">Møte</option>
                  <option value="samtale">Samtale</option>
                  <option value="e-post">E-post</option>
                  <option value="telefon">Telefon</option>
                </optgroup>
                <optgroup label="Arbeid">
                  <option value="dokument">Dokument</option>
                  <option value="research">Research</option>
                  <option value="skriving">Skriving</option>
                  <option value="gjennomgang">Gjennomgang</option>
                </optgroup>
              </select>
            </div>

            <div className="form-group">
              <label>Prioritet</label>
              <select
                value={formData.prioritet}
                onChange={(e) => setFormData({ ...formData, prioritet: e.target.value })}
              >
                <option value="lav">Lav</option>
                <option value="middels">Middels</option>
                <option value="høy">Høy</option>
                <option value="kritisk">Kritisk</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Sak</label>
            <select
              value={formData.sak_id}
              onChange={(e) => setFormData({ ...formData, sak_id: e.target.value })}
            >
              <option value="">-- Ingen sak --</option>
              {saker.map(sak => (
                <option key={sak.id} value={sak.id}>
                  {sak.saksnummer} - {sak.tittel}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Klient (alternativt)</label>
            <select
              value={formData.klient_id}
              onChange={(e) => setFormData({ ...formData, klient_id: e.target.value })}
            >
              <option value="">-- Ingen klient --</option>
              {klienter.map(klient => (
                <option key={klient.id} value={klient.id}>
                  {klient.navn}
                </option>
              ))}
            </select>
            <small>Brukes hvis oppgaven gjelder en klient generelt, ikke en spesifikk sak</small>
          </div>

          <div className="form-group">
            <label>Beskrivelse</label>
            <textarea
              value={formData.beskrivelse}
              onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
              rows="3"
              placeholder="Detaljer om oppgaven..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Forfallsdato</label>
              <input
                type="date"
                value={formData.forfall_dato}
                onChange={(e) => setFormData({ ...formData, forfall_dato: e.target.value })}
              />
              <div style={{marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                <input
                  type="checkbox"
                  id="kritisk-frist"
                  checked={formData.kritisk_frist || false}
                  onChange={(e) => setFormData({ ...formData, kritisk_frist: e.target.checked })}
                  style={{width: '18px', height: '18px', cursor: 'pointer'}}
                />
                <label htmlFor="kritisk-frist" style={{cursor: 'pointer', fontWeight: 600, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                  🚨 Kritisk frist (varsler 3 dager før)
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Ansvarlig advokat</label>
              <input
                type="text"
                value={formData.ansvarlig_advokat}
                onChange={(e) => setFormData({ ...formData, ansvarlig_advokat: e.target.value })}
                placeholder="Navn"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Estimerte timer</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.estimert_timer}
                onChange={(e) => setFormData({ ...formData, estimert_timer: parseFloat(e.target.value) })}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="ikke_startet">Ikke startet</option>
                <option value="i_gang">I gang</option>
                <option value="fullført">Fullført</option>
                <option value="blokkert">Blokkert</option>
              </select>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Avbryt
            </button>
            <button type="submit" className="btn-primary">
              💾 Lagre
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Hjelpefunksjon for å få riktig ikon per type
function getTypeIkon(type) {
  const ikoner = {
    // Prosessdokumenter
    'rettsmøte': '⚖️',
    'stevning': '📜',
    'tilsvar': '📝',
    'prosesskriv': '📋',
    'spesifisert_krav': '📊',
    'søksmålsvarsel': '⚠️',
    'avsluttet_saksforberedelse': '✅',
    'sluttinnlegg': '🏛️',
    // Kommunikasjon
    'møte': '🤝',
    'samtale': '💬',
    'e-post': '📧',
    'telefon': '📞',
    // Arbeid
    'dokument': '📄',
    'research': '🔍',
    'skriving': '✍️',
    'gjennomgang': '👁️',
    'todo': '📋'
  };
  return ikoner[type] || '🏷️';
}
